/**
 * MPS Search Refinements
 * Adds Manufacturer + Category pill filters to the search results page and
 * owns ALL in-place filtering on this page (bike finder + pills).
 *
 * - Multi-select: pick several Manufacturers and several Categories at once.
 *   Within a group choices combine with OR; the two groups combine with AND.
 * - Pills with zero products for the current combination are hidden, and
 *   counts update live as other filters change.
 * - When the bike finder loads a bike's full range via AJAX, the bar rebuilds
 *   itself against the new products and keeps your selections.
 */
(function () {
  'use strict';

  function escapeText(value) {
    return String(value == null ? '' : value).replace(/[<>&"]/g, '');
  }

  function normalize(value) {
    return String(value || '')
      .toLowerCase()
      .replace(/&/g, 'and')
      .replace(/[^a-z0-9]+/g, ' ')
      .replace(/\s+/g, ' ')
      .trim();
  }

  // Lightweight GA4/GTM tracking so finder & pill usage is measurable
  function track(event, data) {
    try {
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push(Object.assign({ event: event }, data || {}));
    } catch (error) {}
  }

  function init() {
    var container = document.getElementById('filter-results');
    if (!container || container.dataset.mpsRefineInit === 'true') return;
    container.dataset.mpsRefineInit = 'true';

    var bar = null;
    var groupsBox = null;
    var status = null;
    var items = [];
    var itemTexts = [];
    var state = { vendors: [], types: [], bike: null };

    function rescan() {
      items = Array.prototype.slice.call(container.querySelectorAll('li[data-search-item]'));
      itemTexts = items.map(function (item) {
        return normalize(item.getAttribute('data-search-text') || item.textContent);
      });
    }

    function currentPattern() {
      if (!state.bike) return null;
      var anyExact = itemTexts.some(function (text) {
        return text.indexOf(state.bike.full) !== -1;
      });
      return anyExact ? state.bike.full : state.bike.partial;
    }

    function matchesAll(item, index, pattern, skipKind) {
      if (pattern && itemTexts[index].indexOf(pattern) === -1) return false;
      if (skipKind !== 'vendor' && state.vendors.length &&
          state.vendors.indexOf((item.getAttribute('data-vendor') || '').trim()) === -1) return false;
      if (skipKind !== 'type' && state.types.length &&
          state.types.indexOf((item.getAttribute('data-product-type') || '').trim()) === -1) return false;
      return true;
    }

    // ---- Single source of truth for visibility ----
    function applyAll() {
      var pattern = currentPattern();
      var visible = 0;
      items.forEach(function (item, index) {
        var show = matchesAll(item, index, pattern, null);
        item.style.display = show ? '' : 'none';
        if (show) visible++;
      });
      refreshPills(pattern);
      updateStatus(visible);
    }

    // Hide pill combinations that would show 0 products; update live counts
    function refreshPills(pattern) {
      if (!bar) return;
      bar.querySelectorAll('.mps-refine-pill').forEach(function (pill) {
        var kind = pill.getAttribute('data-kind');
        var value = pill.getAttribute('data-value');
        var count = 0;
        items.forEach(function (item, index) {
          var saved = state[kind];
          state[kind] = [value];
          if (matchesAll(item, index, pattern, kind)) count++;
          state[kind] = saved;
        });
        pill.style.display = count === 0 ? 'none' : '';
        var countEl = pill.querySelector('.mps-refine-pill__count');
        if (countEl) countEl.textContent = count;
      });
    }

    function activeLabels() {
      var active = [];
      if (state.bike) active.push(state.bike.label);
      if (state.vendors.length) active.push(state.vendors.map(escapeText).join(' / '));
      if (state.types.length) active.push(state.types.map(escapeText).join(' / '));
      return active;
    }

    function updateStatus(visible) {
      if (!status) return;
      var active = activeLabels();
      var html = 'Showing <strong>' + visible + '</strong> of ' + items.length + ' results';
      if (active.length) html += ' for ' + active.join(' + ');
      html += active.length
        ? '. <button type="button" class="mps-refine-clear" data-mps-refine-clear>Show all</button>'
        : '.';
      status.innerHTML = html;
    }

    function clearAll() {
      var hadBike = !!state.bike;
      state.vendors = [];
      state.types = [];
      state.bike = null;
      if (bar) {
        bar.querySelectorAll('.mps-refine-pill.is-active').forEach(function (pill) {
          pill.classList.remove('is-active');
        });
      }
      if (hadBike) {
        // Let the bike finder reset its own message
        document.dispatchEvent(new CustomEvent('mps:bike-cleared'));
      }
      applyAll();
    }

    function buildGroup(label, entries, kind) {
      if (entries.length < 2) return;
      var group = document.createElement('div');
      group.className = 'mps-refine-group';

      var head = document.createElement('span');
      head.className = 'mps-refine-group__label';
      head.textContent = label;
      group.appendChild(head);

      var row = document.createElement('div');
      row.className = 'mps-refine-group__pills';
      entries.forEach(function (entry) {
        var pill = document.createElement('button');
        pill.type = 'button';
        pill.className = 'mps-refine-pill';
        pill.setAttribute('data-kind', kind);
        pill.setAttribute('data-value', entry.name);
        pill.innerHTML = escapeText(entry.name) +
          ' <span class="mps-refine-pill__count">' + entry.count + '</span>';
        pill.addEventListener('click', function () {
          var list = state[kind];
          var pos = list.indexOf(entry.name);
          var cleared = pos !== -1;
          if (cleared) {
            list.splice(pos, 1);
            pill.classList.remove('is-active');
          } else {
            list.push(entry.name);
            pill.classList.add('is-active');
          }
          track('search_refine', {
            refine_type: kind,
            refine_value: entry.name,
            refine_cleared: cleared,
            refine_selected: list.join(' | ')
          });
          applyAll();
        });
        row.appendChild(pill);
      });
      group.appendChild(row);
      groupsBox.appendChild(group);
    }

    function buildBar() {
      bar = document.createElement('div');
      bar.className = 'mps-refine-bar';
      var title = document.createElement('p');
      title.className = 'mps-refine-bar__title';
      title.textContent = 'Refine these results';
      bar.appendChild(title);

      groupsBox = document.createElement('div');
      bar.appendChild(groupsBox);

      status = document.createElement('p');
      status.className = 'mps-refine-status';
      status.setAttribute('role', 'status');
      bar.appendChild(status);

      bar.addEventListener('click', function (event) {
        if (!event.target.closest('[data-mps-refine-clear]')) return;
        track('search_refine', { refine_cleared: true });
        clearAll();
      });

      // Bike finder coordination (bar owns all filtering when present)
      document.addEventListener('mps:bike-selected', function (event) {
        var detail = event.detail || {};
        if (!detail.full || !detail.partial) return;
        state.bike = { full: detail.full, partial: detail.partial, label: detail.label || '' };
        track('search_bike_filter', { bike: state.bike.label });
        applyAll();
      });
      document.addEventListener('mps:bike-cleared', function () {
        state.bike = null;
        applyAll();
      });

      var anchor = document.querySelector('.cc-main-search .bike-finder-toolbar-wrapper') ||
                   document.querySelector('.cc-main-search .main-products-grid');
      if (anchor && anchor.parentNode) {
        anchor.parentNode.insertBefore(bar, anchor.nextSibling);
      } else {
        container.parentNode.insertBefore(bar, container);
      }
    }

    function rebuildGroups() {
      if (!groupsBox) return;
      groupsBox.innerHTML = '';
      var vendorCounts = {};
      var typeCounts = {};
      items.forEach(function (item) {
        var vendor = (item.getAttribute('data-vendor') || '').trim();
        var type = (item.getAttribute('data-product-type') || '').trim();
        if (vendor) vendorCounts[vendor] = (vendorCounts[vendor] || 0) + 1;
        if (type) typeCounts[type] = (typeCounts[type] || 0) + 1;
      });

      function topEntries(counts, max) {
        return Object.keys(counts)
          .map(function (name) { return { name: name, count: counts[name] }; })
          .sort(function (a, b) { return b.count - a.count || a.name.localeCompare(b.name); })
          .slice(0, max);
      }

      // "Vendor" is labelled "Manufacturer" in the UI (store terminology).
      buildGroup('Manufacturer', topEntries(vendorCounts, 12), 'vendors');
      buildGroup('Category', topEntries(typeCounts, 10), 'types');
    }

    // Rebuild everything against the current products in the container
    // (used on first load AND after the bike finder AJAX-injects new results).
    // Manufacturer/Category selections are PRESERVED when those options still
    // exist in the new results.
    function rebuild() {
      var prevVendors = state.vendors.slice();
      var prevTypes = state.types.slice();

      rescan();
      state.vendors = [];
      state.types = [];
      state.bike = null;

      if (items.length < 2) {
        if (bar) bar.style.display = 'none';
        return;
      }

      if (!bar) buildBar();
      bar.style.display = '';
      rebuildGroups();

      // Restore previous pill selections against the new product set
      if (prevVendors.length || prevTypes.length) {
        bar.querySelectorAll('.mps-refine-pill').forEach(function (pill) {
          var kind = pill.getAttribute('data-kind');
          var value = pill.getAttribute('data-value');
          if (kind === 'vendors' && prevVendors.indexOf(value) !== -1) {
            state.vendors.push(value);
            pill.classList.add('is-active');
          }
          if (kind === 'types' && prevTypes.indexOf(value) !== -1) {
            state.types.push(value);
            pill.classList.add('is-active');
          }
        });
      }

      applyAll();
    }

    // The bike finder dispatches this after AJAX-loading a bike's full range
    document.addEventListener('mps:bike-results-loaded', rebuild);

    rebuild();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
